// features/projects/templates/continuum/publications.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM PUBLICATIONS — generated outputs from research data
//
// Three formats: paper outline, policy brief, talk outline.
// Each pulls from the workspace's findings + hypotheses +
// predictions + open questions, runs through Claude with a
// format-specific prompt, and returns markdown.
//
// Output is rendered in a scrollable area with a copy-to-clipboard
// button. Generation is on-demand — nothing is persisted (yet).
// Cost: ~$0.05–$0.15 per generation.
//
// Sessions 9-12 of the Continuum-actionability extension.
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import { C, FONT as F } from './tokens';

const FORMATS = [
  {
    key: 'paper-outline',
    label: 'Paper Outline',
    description: 'Academic paper structure: abstract, methodology, findings, discussion.',
    cost: '~$0.10',
  },
  {
    key: 'policy-brief',
    label: 'Policy Brief',
    description: '1-page funder/policymaker format. Plain English, action-oriented.',
    cost: '~$0.05',
  },
  {
    key: 'talk-outline',
    label: 'Talk Outline',
    description: '15-min lecture: hook, 3 acts, takeaway. Slide-by-slide.',
    cost: '~$0.08',
  },
];

export default function ContinuumPublications({ workspaceId }) {
  const [activeFormat, setActiveFormat] = useState(null);
  const [output, setOutput] = useState(null);  // { format, markdown, generated_at, model }
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async (format) => {
    if (generating) return;
    setActiveFormat(format);
    setGenerating(true);
    setError(null);
    setCopied(false);
    try {
      const r = await fetch('/api/continuum/publications/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, format }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      const j = await r.json();
      // Strip markdown backslash-escapes from identifier-style
      // tokens (mvp\_threshold, keystone\_cascade, etc.). Modern
      // renderers (GitHub, Notion, Obsidian) handle bare under-
      // scores in identifiers fine — the escapes are noise that
      // also breaks the readable preview in this <pre> panel.
      const cleaned = {
        ...j,
        markdown: typeof j.markdown === 'string'
          ? j.markdown.replace(/\\([_*`])/g, '$1')
          : j.markdown,
      };
      setOutput(cleaned);
    } catch (err) {
      setError(err.message);
      setOutput(null);
    } finally {
      setGenerating(false);
    }
  };

  const copy = async () => {
    if (!output?.markdown) return;
    try {
      await navigator.clipboard.writeText(output.markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Clipboard write failed — select text manually.');
    }
  };

  return (
    <div style={{
      height: '100%', background: C.bg, color: C.text,
      fontFamily: F.mono, fontSize: 12,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <Header />
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '18px 22px' }}>
        <FormatPicker
          formats={FORMATS}
          active={activeFormat}
          generating={generating}
          onPick={generate}
        />

        {error && (
          <div style={{
            marginTop: 18, padding: '10px 14px',
            background: 'rgba(196,80,80,.08)',
            border: '1px solid rgba(196,80,80,.40)', borderRadius: 4,
            fontSize: 12, color: C.terra, fontFamily: F.mono,
          }}>
            Error: {error}
          </div>
        )}

        {generating && (
          <div style={{
            marginTop: 18, padding: '40px 20px', textAlign: 'center',
            color: C.dim, fontFamily: F.mono, fontSize: 12,
            border: `1px dashed ${C.b1}`, borderRadius: 5,
          }}>
            Generating {activeFormat?.replace('-', ' ')}… ~15-30 seconds.
          </div>
        )}

        {output && !generating && (
          <OutputPanel output={output} onCopy={copy} copied={copied} />
        )}

        {!output && !generating && !error && (
          <div style={{
            marginTop: 40, textAlign: 'center', color: C.dim,
            fontFamily: F.sans, fontSize: 13, lineHeight: 1.6,
            maxWidth: 600, margin: '40px auto',
          }}>
            Pick a format above to generate from this workspace's
            findings, hypotheses, predictions, and open questions.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────
// Matches features/projects/templates/continuum/findings.js Header
// pattern: pill label + context line.
function Header() {
  const accent = C.purple || C.gold;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '0 20px', height: 42,
      borderBottom: `1px solid ${C.b1}`, background: C.s1, flexShrink: 0, gap: 14,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 9, padding: '3px 10px', borderRadius: 4,
        background: 'rgba(155,143,196,.10)', border: '1px solid rgba(155,143,196,.25)',
        color: accent,
      }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: accent }} />
        Publications — Generated Outputs
      </div>
      <div style={{ fontSize: 9, color: C.dim }}>
        From the workspace's research data
      </div>
    </div>
  );
}

function FormatPicker({ formats, active, generating, onPick }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12,
    }}>
      {formats.map(f => {
        const isActive = active === f.key;
        return (
          <button
            key={f.key}
            type="button"
            onClick={() => onPick(f.key)}
            disabled={generating}
            style={{
              textAlign: 'left',
              padding: '14px',
              background: isActive ? `${C.purple || C.gold}15` : 'rgba(255,255,255,.02)',
              border: `1px solid ${isActive ? (C.purple || C.gold) : C.b1}`,
              borderRadius: 5,
              cursor: generating ? 'wait' : 'pointer',
              transition: 'all 0.12s',
              fontFamily: F.sans,
              color: C.text,
            }}
            onMouseEnter={(e) => {
              if (!generating && !isActive) {
                e.currentTarget.style.borderColor = C.b2;
                e.currentTarget.style.background = 'rgba(255,255,255,.04)';
              }
            }}
            onMouseLeave={(e) => {
              if (!generating && !isActive) {
                e.currentTarget.style.borderColor = C.b1;
                e.currentTarget.style.background = 'rgba(255,255,255,.02)';
              }
            }}
          >
            <div style={{
              fontFamily: F.mono, fontSize: 9, letterSpacing: '0.16em',
              color: isActive ? (C.purple || C.gold) : C.dim,
              textTransform: 'uppercase', marginBottom: 6, fontWeight: 600,
            }}>
              ◆ {f.label}
            </div>
            <div style={{
              fontSize: 12, color: C.text, lineHeight: 1.5, marginBottom: 6,
            }}>
              {f.description}
            </div>
            <div style={{
              fontSize: 10, color: C.dim, fontFamily: F.mono,
              letterSpacing: '0.04em',
            }}>
              {f.cost}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function OutputPanel({ output, onCopy, copied }) {
  const formatLabel = FORMATS.find(f => f.key === output.format)?.label || output.format;
  const handleDownload = () => {
    const ts = new Date(output.generated_at).toISOString().replace(/[:T]/g, '-').slice(0, 16);
    const filename = `continuum-${output.format}-${ts}.md`;
    const blob = new Blob([output.markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${C.b1}`,
        gap: 12, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{
            fontFamily: F.mono, fontSize: 9, letterSpacing: '0.16em',
            color: C.purple || C.gold, textTransform: 'uppercase', fontWeight: 600,
          }}>
            ◆ {formatLabel}
          </div>
          <div style={{
            fontFamily: F.mono, fontSize: 9, color: C.dim,
            letterSpacing: '0.04em', marginTop: 3,
          }}>
            generated {new Date(output.generated_at).toLocaleString()} · {output.model}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleDownload}
            title="Download as .md file"
            style={{
              background: 'transparent',
              border: `1px solid ${C.b2}`, borderRadius: 4,
              color: C.dim,
              fontFamily: F.mono, fontSize: 10, letterSpacing: '0.06em',
              padding: '5px 14px', cursor: 'pointer',
              textTransform: 'uppercase',
            }}
          >
            ↓ Download .md
          </button>
          <button
            onClick={onCopy}
            style={{
              background: copied ? C.sage : 'transparent',
              border: `1px solid ${copied ? C.sage : C.b2}`, borderRadius: 4,
              color: copied ? '#1a1a1a' : C.dim,
              fontFamily: F.mono, fontSize: 10, letterSpacing: '0.06em',
              padding: '5px 14px', cursor: 'pointer',
              textTransform: 'uppercase', fontWeight: 600,
            }}
          >
            {copied ? '✓ Copied' : 'Copy markdown'}
          </button>
        </div>
      </div>
      <div style={{
        background: 'rgba(255,255,255,.02)',
        border: `1px solid ${C.b1}`, borderRadius: 5,
        padding: '24px 28px', maxWidth: 820,
      }}>
        <MarkdownRender text={output.markdown} />
      </div>
    </div>
  );
}

// ─── Markdown subset renderer ─────────────────────────────────
// Handles the subset of markdown that the publication generator
// emits: # / ## / ### headings, **bold**, *italic*, `code`,
// bullet lists (-, *), numbered lists (1.), horizontal rules (---),
// blockquotes (>), and paragraphs separated by blank lines.
//
// Not a full markdown spec — keeps the surface area small. If the
// generator starts emitting tables, links, or images and they need
// to render, extend this here.
function MarkdownRender({ text }) {
  if (!text) return null;
  const lines = String(text).split('\n');
  const blocks = [];
  let bullets = [];
  let numbered = [];
  let quotes = [];
  let para = [];

  const flushBullets = () => {
    if (!bullets.length) return;
    const items = bullets;
    bullets = [];
    blocks.push(
      <ul key={`ul-${blocks.length}`} style={{
        margin: '6px 0 14px 0', paddingLeft: 22,
        color: C.text, listStyle: 'disc',
      }}>
        {items.map((b, i) => (
          <li key={i} style={{ marginBottom: 6, lineHeight: 1.7 }}>
            {renderInline(b)}
          </li>
        ))}
      </ul>
    );
  };
  const flushNumbered = () => {
    if (!numbered.length) return;
    const items = numbered;
    numbered = [];
    blocks.push(
      <ol key={`ol-${blocks.length}`} style={{
        margin: '6px 0 14px 0', paddingLeft: 26,
        color: C.text, listStyle: 'decimal',
      }}>
        {items.map((b, i) => (
          <li key={i} style={{ marginBottom: 6, lineHeight: 1.7 }}>
            {renderInline(b)}
          </li>
        ))}
      </ol>
    );
  };
  const flushQuotes = () => {
    if (!quotes.length) return;
    const items = quotes;
    quotes = [];
    blocks.push(
      <blockquote key={`bq-${blocks.length}`} style={{
        margin: '12px 0', padding: '4px 0 4px 14px',
        borderLeft: `2px solid ${C.b2}`,
        color: C.mid, fontStyle: 'italic',
        lineHeight: 1.7,
      }}>
        {items.map((q, i) => (
          <div key={i} style={{ marginBottom: i < items.length - 1 ? 6 : 0 }}>
            {renderInline(q)}
          </div>
        ))}
      </blockquote>
    );
  };
  const flushPara = () => {
    if (!para.length) return;
    const text = para.join(' ').trim();
    para = [];
    if (!text) return;
    blocks.push(
      <p key={`p-${blocks.length}`} style={{
        margin: '0 0 14px 0', lineHeight: 1.75, color: C.text,
        fontFamily: 'Georgia, serif', fontSize: 14,
      }}>
        {renderInline(text)}
      </p>
    );
  };
  const flushAll = () => {
    flushBullets(); flushNumbered(); flushQuotes(); flushPara();
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');

    // blank line — paragraph break
    if (!line.trim()) { flushAll(); continue; }

    // horizontal rule
    if (/^---+\s*$/.test(line.trim())) {
      flushAll();
      blocks.push(
        <hr key={`hr-${blocks.length}`} style={{
          margin: '20px 0', border: 'none', borderTop: `1px solid ${C.b1}`,
        }} />
      );
      continue;
    }

    // headings
    let m;
    if ((m = line.match(/^#\s+(.*)/))) {
      flushAll();
      blocks.push(
        <h1 key={`h1-${blocks.length}`} style={{
          margin: '24px 0 12px 0', fontSize: 22, fontWeight: 600,
          fontFamily: 'Georgia, serif', color: C.text, lineHeight: 1.3,
        }}>{renderInline(m[1])}</h1>
      );
      continue;
    }
    if ((m = line.match(/^##\s+(.*)/))) {
      flushAll();
      blocks.push(
        <h2 key={`h2-${blocks.length}`} style={{
          margin: '22px 0 10px 0', fontSize: 18, fontWeight: 600,
          fontFamily: 'Georgia, serif', color: C.text, lineHeight: 1.35,
        }}>{renderInline(m[1])}</h2>
      );
      continue;
    }
    if ((m = line.match(/^###\s+(.*)/))) {
      flushAll();
      blocks.push(
        <h3 key={`h3-${blocks.length}`} style={{
          margin: '18px 0 8px 0', fontSize: 15, fontWeight: 600,
          fontFamily: 'Georgia, serif', color: C.text, lineHeight: 1.4,
        }}>{renderInline(m[1])}</h3>
      );
      continue;
    }

    // blockquote
    if ((m = line.match(/^>\s?(.*)/))) {
      flushBullets(); flushNumbered(); flushPara();
      quotes.push(m[1]);
      continue;
    }

    // bullet list
    if ((m = line.match(/^\s*[-*]\s+(.+)/))) {
      flushNumbered(); flushQuotes(); flushPara();
      bullets.push(m[1]);
      continue;
    }

    // numbered list
    if ((m = line.match(/^\s*\d+\.\s+(.+)/))) {
      flushBullets(); flushQuotes(); flushPara();
      numbered.push(m[1]);
      continue;
    }

    // regular line — append to paragraph buffer
    flushBullets(); flushNumbered(); flushQuotes();
    para.push(line.trim());
  }
  flushAll();

  return <>{blocks}</>;
}

// Inline markdown: **bold**, *italic*, `code`. Order matters —
// **bold** before *italic* to avoid matching the ** as italic.
function renderInline(text) {
  if (!text) return null;
  const parts = String(text).split(/(`[^`\n]+`|\*\*[^*\n]+\*\*|(?<!\*)\*[^*\n]+\*(?!\*))/g);
  return parts.map((seg, i) => {
    if (!seg) return null;
    if (seg.startsWith('`') && seg.endsWith('`') && seg.length > 2) {
      return (
        <code key={i} style={{
          fontFamily: F.mono, fontSize: '0.9em',
          background: 'rgba(255,255,255,.05)',
          border: `1px solid ${C.b1}`, borderRadius: 3,
          padding: '1px 5px', color: C.text,
        }}>{seg.slice(1, -1)}</code>
      );
    }
    if (seg.startsWith('**') && seg.endsWith('**') && seg.length > 4) {
      return <strong key={i} style={{ fontWeight: 700, color: C.text }}>{seg.slice(2, -2)}</strong>;
    }
    if (seg.startsWith('*') && seg.endsWith('*') && seg.length > 2) {
      return <em key={i} style={{ fontStyle: 'italic' }}>{seg.slice(1, -1)}</em>;
    }
    return <span key={i}>{seg}</span>;
  }).filter(Boolean);
}
